import { Badge, Button, Modal, Textarea, useToast } from '@ohear/ui'
/**
 * Modal for requesting a pickup re-record.
 *
 * Wraps the supervisor decision endpoint with `decision='rejected_pickup'` and
 * a free-text note that's surfaced to the actor in their pickup banner. The
 * red severity here is intentional — pickup is one of the few places the UI
 * is allowed to use red per the friendly-tone rules.
 */
import { useEffect, useId, useState } from 'react'
import { useDecideTake } from '../../lib/api/supervisor'
import type { PendingTake } from '../../lib/api/supervisor'

export interface PickupRequestModalProps {
  projectId: string
  take: PendingTake | null
  onClose: () => void
  /** Called after the decision succeeds. */
  onDecided?: () => void
}

export function PickupRequestModal({
  projectId,
  take,
  onClose,
  onDecided,
}: PickupRequestModalProps) {
  const decide = useDecideTake(projectId)
  const toast = useToast()
  const [note, setNote] = useState('')
  const noteId = useId()

  // Reset note text whenever a different take is opened.
  useEffect(() => {
    setNote('')
  }, [take?.take_id])

  if (!take) return null

  const submit = () => {
    decide.mutate(
      { takeId: take.take_id, decision: 'rejected_pickup', supervisor_note: note.trim() || null },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            title: 'Pickup requested',
            description: `${take.character_display_name} cue ${take.cue_number} sent back for re-record.`,
          })
          onDecided?.()
          onClose()
        },
        onError: (err) => {
          toast.show({
            severity: 'error',
            title: 'Could not request pickup',
            description: err instanceof Error ? err.message : 'Unknown error',
          })
        },
      },
    )
  }

  return (
    <Modal
      open={Boolean(take)}
      onClose={onClose}
      title="Request pickup"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} loading={decide.isPending}>
            Request pickup
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-3)] p-3">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="gray">{take.character_code}</Badge>
            <span className="font-medium">{take.character_display_name}</span>
            <span className="text-[var(--text-2)]">
              cue {take.cue_number} - take v{take.version}
            </span>
          </div>
          <p className="text-[13px] text-[var(--text-2)] m-0">{take.cue_text}</p>
        </div>

        <div>
          <label htmlFor={noteId} className="block text-[13px] font-medium text-[var(--text)] mb-1">
            Note for the actor
          </label>
          <Textarea
            id={noteId}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="What should they change? Keep it specific and kind."
            aria-describedby={`${noteId}-hint`}
            disabled={decide.isPending}
          />
          <p id={`${noteId}-hint`} className="text-[12px] text-[var(--text-2)] mt-1 m-0">
            Shown verbatim in the actor's pickup banner. They'll see your name next to it.
          </p>
        </div>
      </div>
    </Modal>
  )
}
